<template>
<Page>
    <h1>Terms of Service</h1>

    <p>
        Welcome to coroot.com (together with any related websites, the “Site”).
        The Site is owned and operated by Coroot, Inc. (“Coroot”).
        Through the Site, Coroot provides to end-users cloud-hosted application and infrastructure monitoring service as well as other services
        (the “Service”).
        Please read these Terms of Service (these “Terms”) carefully before using the Site or the Service.
    </p>

    <p>
        By accessing or using the Site or the Service you agree to be legally bound by these Terms.
        If you do not accept these Terms, do not use the Site or the Service.
    </p>

    <h2>Account Terms</h2>

    <p>
        You are responsible for maintaining the security of your account and password.
        Coroot cannot and will not be liable for any loss or damage from your failure to comply with this security obligation.
        You are responsible for all content posted and activity that occurs under your account (even when content is posted by others who have their own logins under your account).
        You may not use the Service for any illegal purpose or to violate any laws in your jurisdiction (including but not limited to copyright laws).
        You must provide your legal full name, a valid email address, and any other information requested in order to complete the signup process.
        Your login may only be used by one person – a single login shared by multiple people is not permitted.
        You may create separate logins for as many people as you’d like.
        You must be a human.
        Accounts registered by “bots” or other automated methods are not permitted.
    </p>

    <h2>API Terms</h2>

    <p>
        Customers may access their Service data via the Application Program Interface (“API”).
        Any use of the API, including use of the API through a third-party product that accesses the Service or the Site,
        is bound by these terms plus the following specific terms: you expressly understand and agree that Coroot
        shall not be liable for any damages or losses resulting from your use of the API or third-party products that access data via the API.
        Abuse or excessively frequent requests to the Service or the Site via the API may result in the temporary or permanent suspension
        of your account’s access to the API.
        Coroot, in its sole discretion, will determine abuse or excessive usage of the API.
        Coroot will make a reasonable attempt via email to warn the account owner prior to suspension.
    </p>

    <h2>Payment, Refunds, Upgrading and Downgrading Terms</h2>

    <p>
        The Service is offered with a free trial for 14 days.
        If you need more than 14 days, you will only be able to continue using the Service by paying in advance for additional usage.
        If you fail to pay for additional usage, your account will be frozen and inaccessible until payment is made.
    </p>

    <p>
        Any upgrade or downgrade in plan level can result in prorated charges.
        Downgrading your Service may cause the loss of features or capacity of your account.
        Coroot does not accept any liability for such loss.
    </p>

    <p>
        All fees are exclusive of all taxes, levies, or duties imposed by taxing authorities,
        and you shall be responsible for payment of all such taxes, levies, or duties, excluding only the United States (federal or state) taxes.
        Where required, Coroot will collect those taxes on behalf of taxing authority and remit those taxes to taxing authorities.
    </p>

    <h2>Cancellation and Termination</h2>

    <p>You are solely responsible for properly canceling your account. You can cancel your account at any time by sending request to support@coroot.com.</p>

    <p>
        All of your content will be inaccessible from the Service immediately upon cancellation.
        Within 30 days, all content will be permanently deleted from backups and logs.
        This information can not be recovered once it has been permanently deleted.
        If you cancel the Service before the end of your current paid up month, your cancellation will take effect immediately,
        and you will not be charged again.
        But there will not be any prorating of unused time in the last billing cycle.
    </p>

    <p>
        Coroot, in its sole discretion, has the right to suspend or terminate your account and refuse any and all current
        or future use of the Service and the Site for any reason at any time.
        Such termination of the Service will result in the deactivation or deletion of your account or your access to your account,
        and the forfeiture and relinquishment of all content in your account.
        Coroot reserves the right to refuse service to anyone for any reason at any time.
    </p>

    <h2>Modifications to the Service and Prices</h2>

    <p>
        Coroot reserves the right at any time and from time to time to modify or discontinue, temporarily or permanently,
        any part of the Service or the Site, with or without notice.
        Prices to the Service, including various Service plans and levels, are subject to change upon 30 days notice from Coroot.
        Such notice may be provided at any time by posting the changes to the Site or the Service.
    </p>

    <p>
        Coroot shall not be liable to you or to any third party for any modification, price change, suspension or
        discontinuance of the Service or the Site.
    </p>

    <h2>General Service Conditions</h2>

    <p>
        You understand that Coroot uses third party vendors and hosting partners to provide the necessary hardware, software,
        networking, storage, and related technology required to run the Service.
    </p>

    <p>
        You must not modify, adapt or hack the Service.
        You must not modify another website so as to falsely imply that it is associated with the Service, the Site or Coroot.
        You agree not to reproduce, duplicate, copy, sell, resell or exploit any portion of the Service or the Site,
        without the express written permission by Coroot.
    </p>

    <p>
        We may, but have no obligation to, remove Content or accounts that we determine in our sole discretion are
        unlawful or violate any party’s intellectual property or these Terms.
    </p>

    <p>
        Verbal, physical, written or other abuse (including threats of abuse or retribution) of any Service customer,
        Coroot employee or officer will result in immediate account termination.
        We reserve the right to temporarily disable your account if your usage significantly exceeds the average usage
        of other Service customers.
        Of course, we’ll reach out to the account owner before taking any action except in rare cases where the level
        of use may negatively impact the performance of the Service for other customers.
    </p>

    <p>
        Technical support is provided only via email.
        You understand that the technical processing and transmission of the Service,
        including any Content, may be transferred unencrypted and involve (a) transmissions over various networks;
        and (b) changes to conform and adapt to technical requirements of connecting networks or devices.
    </p>

    <h2>Intellectual Property Ownership</h2>

    <p>
        The Site and the Service contain HTML, applications, messages, text, files, images, photos, video, sounds, profiles,
        works of authorship and other content (collectively, “Content”) of Coroot or its licensors (“Coroot Content”).
        The Site and the Service (including the Coroot Content) are protected by copyright, trademark, trade secret and other laws;
        and as between you and Coroot, Coroot owns and retains all rights in the Site, the Service and the Coroot Content.
        Coroot hereby grants to you a limited, revocable, non-sublicensable license to access, display and perform the Coroot Content
        (excluding any computer code) solely for your personal, non-commercial use and solely as necessary to access and
        use the Site and the Service.
        Except as expressly permitted by Coroot in these Terms or on the Site, you may not copy, download, stream, capture,
        reproduce, duplicate, archive, upload, modify, translate, create derivative works based upon, publish, broadcast,
        transmit, retransmit, distribute, perform, display, sell or otherwise use or transfer any Coroot Content.
        You may not, either directly or through the use of any device, software, online resource or other means, remove,
        alter, bypass, avoid, interfere with or circumvent any copyright, trademark or other proprietary notice on the Coroot Content
        or any digital rights management mechanism, device, or other content protection or access control measure associated with the Coroot Content.
    </p>

    <p>
        The trademarks, logos and service marks (“Marks”) displayed on the Site, or through the Service, are the property of Coroot or other third parties.
        You are not permitted to use these Marks without the prior written consent of Coroot or such third party.
    </p>

    <p>
        By posting, displaying, publishing or making available for download or use any Content on the Site or through
        the Service (other than personal information that is subject to the Coroot’s Privacy Policy,
        located at https://coroot.com/legal/privacy), you hereby grant Coroot a perpetual, worldwide, nonexclusive,
        irrevocable, royalty-free, sublicensable (through multiple tiers) license to perform, display, reproduce,
        prepare derivative works from, distribute, sell, sublicense, transfer and otherwise use without restriction all or any part of such Content.
    </p>

    <h2>Copyright Infringement</h2>

    <p>
        Coroot respects the intellectual property rights of others and we expect our users to do the same.
        We respond to notices of copyright infringement consistent with the Digital Millennium Copyright Act (“DMCA”).
    </p>

    <h2>The Service is Available “as-is” and without warranty</h2>

    <p>
        Coroot explicitly disclaims any warranties that (a) the Service will meet your specific requirements,
        (b) the Service will be uninterrupted, timely, secure, or error-free,
        (c) the results that may be obtained from the use of the Service will be accurate or reliable,
        (d) the quality of any products, services, information, or other material purchased or obtained by you through the Service or the Site will meet your expectations,
        and (e) any errors in the Service will be corrected.</p>

    <p>
        THE SERVICE, THE SITE, AND ALL CONTENT AND OTHER ITEMS MADE AVAILABLE BY COROOT ARE PROVIDED ON AN “AS IS” AND “AS AVAILABLE”
        BASIS WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE, INCLUDING ANY IMPLIED WARRANTY OF TITLE,
        MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT, TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW.
        COROOT ASSUMES NO LIABILITY OR RESPONSIBILITY FOR ANY (a) ERRORS, MISTAKES OR INACCURACIES OF DATA OR INFORMATION POSTED,
        DISPLAYED, PUBLISHED OR MADE AVAILABLE FOR DOWNLOAD OR USE THROUGH THE SERVICE OR THE SITE, (b) PERSONAL INJURY OR PROPERTY DAMAGE,
        OF ANY NATURE WHATSOEVER, RESULTING FROM USE OF THE SERVICE OR THE SITE,
        (c) ANY INTERRUPTION OR CESSATION OF TRANSMISSION TO OR FROM THE SITE, OR (e) THE DEFAMATORY, OFFENSIVE OR ILLEGAL CONDUCT OF ANY
        THIRD PARTY NOT UNDER COROOT’S CONTROL.
    </p>

    <h2>Limitation of Liability</h2>

    <p>
        TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW: (a) IN NO EVENT SHALL COROOT,
        ITS AFFILIATES OR THEIR RESPECTIVE EMPLOYEES, CONTRACTORS, AGENTS, OFFICERS OR DIRECTORS BE LIABLE FOR ANY INDIRECT,
        PUNITIVE, INCIDENTAL, SPECIAL, CONSEQUENTIAL OR EXEMPLARY DAMAGES, INCLUDING WITHOUT LIMITATION DAMAGES FOR BUSINESS INTERRUPTION,
        LOSS OF PROFITS, GOODWILL, USE, DATA OR OTHER INTANGIBLE LOSSES ARISING OUT OF OR RELATING TO THE USE OF THE SERVICE OR THE SITE;
        AND (b) IN NO EVENT SHALL COROOT’S CUMULATIVE AND AGGREGATE LIABILITY UNDER THESE TERMS EXCEED TWO HUNDRED U.S. DOLLARS.
        THE EXCLUSIONS AND LIMITATIONS IN THIS SECTION APPLY WHETHER THE ALLEGED LIABILITY IS BASED ON CONTRACT, TORT, NEGLIGENCE,
        STRICT LIABILITY OR ANY OTHER BASIS, EVEN IF YOU HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
    </p>

    <h2>Governing Law, Venue and Dispute Resolution</h2>

    <p>
        These Terms shall be governed by and construed and enforced in accordance with the laws of the
        State of Delaware without giving effect to the choice of law rules of that State.
        Any legal action or proceeding arising under or relating to these Terms shall be brought exclusively in the state
        or federal courts located in New Castle County, State of Delaware, and the parties expressly consent
        to personal jurisdiction and venue in those courts.
    </p>

    <p>
        Any dispute, controversy or claim arising out of or in connection with these Terms, or the breach,
        termination or invalidity thereof, shall be settled by binding arbitration administered by JAMS,
        in accordance with the Arbitration Rules and Procedures of JAMS, and judgment on the award
        rendered by the arbitrator(s) may be entered in any court having jurisdiction thereof.
        The arbitration shall take place in New Castle County, State of Delaware.
        The language to be used in the arbitration proceedings shall be English.
    </p>

    <p>
        Nothing contained in these Terms shall deny either party the right to seek injunctive or other equitable relief
        from a court of competent jurisdiction in the context of a bona fide emergency or prospective irreparable
        harm and such an action may be filed and maintained notwithstanding any ongoing arbitration proceeding.
    </p>

    <p>
        BOTH PARTIES KNOWINGLY, VOLUNTARILY, AND INTENTIONALLY WAIVE (TO THE EXTENT PERMITTED BY APPLICABLE LAW)
        ANY RIGHT THE PARTIES MAY HAVE TO A TRIAL BY JURY OF ANY DISPUTE ARISING UNDER OR RELATING TO THESE TERMS.
    </p>

    <h2>Miscellaneous Terms</h2>

    <p>
        These Terms constitute the complete and exclusive statement of the agreement between the parties and supersede all proposals,
        oral or written, and all other communications between the parties relating to the subject matter of these Terms.
        In the event any information posted on the Site from time to time conflicts with any provision of these Terms,
        the applicable provision of these Terms shall control.
    </p>

    <p>
        Any terms and conditions of any other instrument issued by you in connection with these Terms which are in addition to,
        inconsistent with or different from these Terms shall be of no force or effect.
        These Terms do not, and shall not be construed to, create any partnership, joint venture, employer-employee,
        agency or franchisor-franchisee relationship between you and Coroot.

    </p>

    <p>
        You may not assign, transfer or sublicense all or any of your rights or obligations under these Terms without Coroot’s
        express prior written consent. Coroot may assign, transfer or sublicense all or any of Coroot’s rights or obligations
        under these Terms without restriction.

        Any waiver by Coroot of any condition, term or provision of these Terms shall not be construed as a waiver of
        any other condition, term or provision.

        If any provision of these Terms is held invalid or unenforceable,
        the remainder of these Terms shall continue in full force and effect.

        You agree that a printed version of these Terms and of any notice given in electronic form shall be admissible
        in judicial or administrative proceedings based upon or relating to these Terms to the same extent and subject
        to the same conditions as other business documents and records originally generated and maintained in printed form.

        Coroot will not be responsible for failures to fulfill any obligations due to causes beyond its control.
    </p>

    <p>
        Non-English translations of these Terms are provided for convenience only.
        In the event of any ambiguity or conflict between translations, the English version is authoritative and controls.
    </p>

    <h2>Questions</h2>

    <p>
        Please contact Coroot at support@coroot.com with any questions regarding these Terms.
    </p>

</Page>
</template>

<script>
import Page from "@/pages/Page";
export default {
    components: {Page}
}
</script>

<style scoped>
h1 {
    text-align: center;
}
</style>
